
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslationEdit } from '@/contexts/TranslationEditContext';
import { Pencil } from 'lucide-react';

interface TranslationTextProps {
  i18nKey: string;
  className?: string;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  values?: Record<string, any>;
}

export function TranslationText({ i18nKey, className, children, onClick, values }: TranslationTextProps) {
  const { t } = useTranslation();
  const { isEditMode, setEditingKey } = useTranslationEdit();
  
  const handleClick = (e: React.MouseEvent) => {
    if (isEditMode) {
      e.preventDefault();
      e.stopPropagation();
      setEditingKey(i18nKey);
      return;
    }
    
    if (onClick) {
      onClick(e);
    }
  };
  
  // Safely convert the translation result to a string for rendering
  const translatedContent = (): string | number => {
    const translated = values ? t(i18nKey, values) : t(i18nKey);
    
    // Handle different types of translated values
    if (translated === null || translated === undefined) {
      return '';
    } else if (typeof translated === 'string' || typeof translated === 'number') {
      return translated;
    } else if (typeof translated === 'boolean') {
      // Convert boolean to string explicitly to avoid type issues
      return translated === true ? 'true' : 'false';
    } else if (typeof translated === 'object') {
      // Handle objects including arrays by stringifying them
      return JSON.stringify(translated);
    }
    
    // Fallback to string representation
    return String(translated);
  };
  
  return (
    <span 
      className={`${className || ''} ${isEditMode ? 'translation-editable relative group cursor-pointer' : ''}`}
      onClick={handleClick}
      data-i18n-key={isEditMode ? i18nKey : undefined}
    >
      {children || translatedContent()}
      {isEditMode && (
        <span className="ml-1 inline-flex items-center text-primary opacity-0 group-hover:opacity-100 transition-opacity">
          <Pencil className="h-3 w-3" />
        </span>
      )}
    </span>
  );
}
